import React, {
  useState,

  useCallback,
  createContext,
  useContext,
  ReactNode,
} from 'react';
import type {ImageSourcePropType} from 'react-native';
import {api} from '../services/api';

import inicial from '../assets/images/carrinhos/fusquinha.webp';
import intermediario from '../assets/images/carrinhos/golzinho.webp';
import intermediarioPlus from '../assets/images/carrinhos/civicao-app.webp';
import avancado from '../assets/images/carrinhos/lendario-gti-app.webp';
import avancadoPlus from '../assets/images/carrinhos/nave-app.webp';
import lendario from '../assets/images/carrinhos/ferrarion.webp';



// Types
export interface CarrinhoId {
  $oid: string;
}

export interface CarrinhoDisponivel {
  id: CarrinhoId;
  carrinho: string;
  label: string;
  minimo_litros: number;
  maximo_litros: number | null;
  ganho_por_abastecimento: number;
}

export interface CarrinhoAtual {
  carrinho: string;
  label: string;
  ganho_por_abastecimento: number;
  qtd_litros_mes: number;
  litros_mes: number[];
}

export interface CarrinhosResponse {
  carrinho_atual: CarrinhoAtual;
  carrinhos_disponiveis: CarrinhoDisponivel[];
}

// Imagens disponíveis - mapeadas pelos valores da API
export const AVAILABLE_CAR_IMAGES: Record<string, ImageSourcePropType> = {
  inicial: inicial,
  intermediario1: intermediario,
  intermediario2: intermediarioPlus,
  avancado1: avancado,
  avancado2: avancadoPlus,
  lendario: lendario,
};

// Ordem dos carros (valores da API)
export const CAR_ORDER = [
  'inicial',
  'intermediario1',
  'intermediario2',
  'avancado1',
  'avancado2',
  'lendario',
];

interface UseCarrinhosReturn {
  // Estados
  loading: boolean;
  error: boolean;
  status: CarrinhosResponse | undefined;

  // Carro atual
  currentCarKey: string;
  currentCarLabel: string;
  currentCarImage: ImageSourcePropType | undefined;
  currentCarIndex: number;
  currentGanhoCompra: number;
  qtdLitrosMes: number;

  // Próximo carro
  nextCar: CarrinhoDisponivel | undefined;
  nextCarImage: ImageSourcePropType | undefined;
  remainingLitros: number;

  // Progresso
  progress: number;
  totalCars: number;

  // Lista de carros
  carrosDisponiveis: CarrinhoDisponivel[];

  // Helpers
  getCarImage: (carKey: string) => ImageSourcePropType | undefined;
  isCarLocked: (car: CarrinhoDisponivel) => boolean;
  isCurrentCar: (car: CarrinhoDisponivel) => boolean;

  // Ações
  refetch: () => Promise<void>;
  resetCarrinhos: () => void;
}

interface CarrinhosProviderProps {
  children: ReactNode;
}

const CarrinhosContext = createContext<UseCarrinhosReturn>(
  {} as UseCarrinhosReturn,
);

export function CarrinhosProvider({children}: CarrinhosProviderProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [status, setStatus] = useState<CarrinhosResponse>();

  const fetchStatus = useCallback(async () => {


    try {
      setLoading(true);
      setError(false);
      const response = await api.get<CarrinhosResponse>('/status_medalha');
      setStatus(response.data);
    } catch {
      setStatus(undefined);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Dados do carro atual
  const currentCarKey = status?.carrinho_atual.carrinho ?? '';
  const currentCarLabel = status?.carrinho_atual.label ?? '';
  const currentCarImage = AVAILABLE_CAR_IMAGES[currentCarKey];
  // Usar o índice na lista de carrosDisponiveis para o carousel
  const carrosDisponiveis = status?.carrinhos_disponiveis ?? [];
  const currentCarIndex = carrosDisponiveis.findIndex(
    c => c.carrinho === currentCarKey,
  );
  const currentGanhoCompra =
    status?.carrinho_atual.ganho_por_abastecimento ?? 0;
  const qtdLitrosMes = status?.carrinho_atual.qtd_litros_mes ?? 0;

  // Próximo carro
  const nextCar = status?.carrinhos_disponiveis.find(
    c => c.minimo_litros > qtdLitrosMes,
  );
  const nextCarImage = AVAILABLE_CAR_IMAGES[nextCar?.carrinho ?? ''];
  const remainingLitros = nextCar ? nextCar.minimo_litros - qtdLitrosMes : 0;

  // Progresso
  const nextThreshold = nextCar?.minimo_litros ?? (qtdLitrosMes || 1);
  const progress = qtdLitrosMes / nextThreshold;
  const totalCars = carrosDisponiveis.length;

  // Helpers
  const getCarImage = useCallback((carKey: string) => {
    return AVAILABLE_CAR_IMAGES[carKey];
  }, []);

  const isCarLocked = useCallback(
    (car: CarrinhoDisponivel) => {
      return qtdLitrosMes < car.minimo_litros;
    },
    [qtdLitrosMes],
  );

  const isCurrentCar = useCallback(
    (car: CarrinhoDisponivel) => {
      return car.carrinho === currentCarKey;
    },
    [currentCarKey],
  );

  const resetCarrinhos = useCallback(() => {
    setStatus(undefined);
    setLoading(false);
    setError(false);
  }, []);

  return (
    <CarrinhosContext.Provider
      value={{
        // Estados
        loading,
        error,
        status,

        // Carro atual
        currentCarKey,
        currentCarLabel,
        currentCarImage,
        currentCarIndex,
        currentGanhoCompra,
        qtdLitrosMes,

        // Próximo carro
        nextCar,
        nextCarImage,
        remainingLitros,

        // Progresso
        progress,
        totalCars,

        // Lista de carros
        carrosDisponiveis,

        // Helpers
        getCarImage,
        isCarLocked,
        isCurrentCar,

        // Ações
        refetch: fetchStatus,
        resetCarrinhos,
      }}>
      {children}
    </CarrinhosContext.Provider>
  );
}

export function useCarrinhos(): UseCarrinhosReturn {
  const context = useContext(CarrinhosContext);
  return context;
}
